// src/components/LimpezaConversasDialog.js
//
// Out/26. A Pontal Seafood chegou a 1.641 conversas ativas num chip só.
// Ninguém lê isso, e o chat passou a carregar só as 500 mais recentes — as
// outras 1.141 ficavam fora da tela sem jeito de resolver uma a uma.
//
// ─── O que este diálogo NÃO faz ────────────────────────────────────────
// Não apaga contato. A limpeza marca as MENSAGENS daquele chip como
// apagadas; o contato continua em Contatos com nota, etiqueta, pasta e todo
// o histórico de CRM. É o modelo do WhatsApp — limpar a conversa não apaga
// quem é a pessoa, e se ela escrever de novo a conversa reaparece sozinha.
//
// Isso está escrito na tela, não só aqui: é a primeira dúvida de quem vê um
// botão chamado "limpar" ao lado de mil e poucas conversas.
//
// ─── Simular antes de aplicar ──────────────────────────────────────────
// Toda escolha passa por uma contagem no servidor antes de virar ação. O
// número que aparece no botão é o que o backend respondeu, não uma conta do
// frontend: limpar 1.141 conversas é irreversível na prática, e um número
// estimado errado seria pior que não mostrar número nenhum.
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Button, Dialog, DialogContent,
  CircularProgress, TextField, Radio,
} from '@mui/material';
import { FiTrash2, FiX, FiAlertTriangle, FiArchive } from 'react-icons/fi';
import api from '../services/Api';

const SYNE = "'Syne', sans-serif";
const OUTFIT = "'Outfit', sans-serif";

const C = {
  card: '#FFFFFF', surf: '#F7F9FC', inset: '#EEF1F7',
  bdr: '#E2E8F0', bdr2: '#CBD5E1',
  text: '#1A2535', text2: '#4B5E72', text3: '#94A3B8',
  green: '#00B865', greenD: '#006B3F', greenBg: '#E6FAF2', greenB: '#A7F0D2',
  amber: '#D97706', amberD: '#92400E', amberBg: '#FFFBEB', amberB: '#FDE68A',
  red: '#DC2626', redD: '#7F1D1D', redBg: '#FEF2F2', redB: '#FECACA',
};

// Atalhos de data. Em meses, não em dias: ninguém pensa "sem falar há 90
// dias", pensa "desde antes de julho".
const PERIODOS = [
  { meses: 1, rotulo: 'há mais de 1 mês' },
  { meses: 3, rotulo: 'há mais de 3 meses' },
  { meses: 6, rotulo: 'há mais de 6 meses' },
];

function dataDeMesesAtras(meses) {
  const d = new Date();
  d.setMonth(d.getMonth() - meses);
  return d;
}

function Opcao({ ativa, onClick, titulo, descricao, children }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        border: `1px solid ${ativa ? C.greenB : C.bdr}`,
        bgcolor: ativa ? C.greenBg : C.card,
        borderRadius: '8px', p: 1.3, mb: 1, cursor: 'pointer',
        transition: 'border-color .15s, background-color .15s',
        '&:hover': { borderColor: ativa ? C.green : C.bdr2 },
      }}
    >
      <Radio checked={ativa} size="small" sx={{
        p: 0, mt: '2px',
        color: C.bdr2, '&.Mui-checked': { color: C.green },
      }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontFamily: SYNE, fontSize: '0.8rem', fontWeight: 700, color: C.text,
        }}>
          {titulo}
        </Typography>
        <Typography sx={{
          fontFamily: SYNE, fontSize: '0.68rem', color: C.text2, lineHeight: 1.5, mt: 0.2,
        }}>
          {descricao}
        </Typography>
        {ativa && children}
      </Box>
    </Box>
  );
}

export default function LimpezaConversasDialog({
  aberto, onFechar, deviceId, total, carregadas, onLimpou,
}) {
  const [modo, setModo] = useState('ALEM_DAS_RECENTES');
  const [meses, setMeses] = useState(3);
  const [manter, setManter] = useState(carregadas || 500);
  const [previa, setPrevia] = useState(null);     // quantas serão limpas
  const [contando, setContando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erro, setErro] = useState('');

  const montarCorpo = useCallback((simular) => {
    if (modo === 'ANTES_DE') {
      return { modo, data: dataDeMesesAtras(meses).toISOString(), simular };
    }
    return { modo: 'ALEM_DAS_RECENTES', manter: Number(manter) || 500, simular };
  }, [modo, meses, manter]);

  // Reconta a cada mudança de escolha. Debounce curto porque o campo
  // "manter" é digitável e recontar a cada tecla seria uma requisição por
  // caractere.
  useEffect(() => {
    if (!aberto || !deviceId) return undefined;
    let vivo = true;
    setContando(true);
    setErro('');
    const t = setTimeout(() => {
      api.post(`/conversations/device/${deviceId}/limpar-antigas`, montarCorpo(true))
        .then(({ data }) => { if (vivo) setPrevia(data?.conversas ?? 0); })
        .catch((e) => {
          if (vivo) {
            setPrevia(null);
            setErro(e?.response?.data?.detail || 'Não consegui calcular.');
          }
        })
        .finally(() => { if (vivo) setContando(false); });
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [aberto, deviceId, montarCorpo]);

  const aplicar = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post(
        `/conversations/device/${deviceId}/limpar-antigas`, montarCorpo(false),
      );
      onLimpou && onLimpou(data?.conversas ?? 0);
      onFechar();
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Não consegui limpar as conversas.');
    } finally {
      setAplicando(false);
    }
  };

  const foraDaLista = Math.max(0, (total || 0) - (carregadas || 0));

  return (
    <Dialog open={!!aberto} onClose={onFechar} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogContent sx={{ p: 2.4 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '7px', bgcolor: C.amberBg,
              border: `1px solid ${C.amberB}`, color: C.amberD,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FiArchive size={14} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: SYNE, fontSize: '0.92rem', fontWeight: 800, color: C.text }}>
                Limpar conversas antigas
              </Typography>
              <Typography sx={{ fontFamily: SYNE, fontSize: '0.68rem', color: C.text3 }}>
                {(total || 0).toLocaleString('pt-BR')} conversas neste chip
              </Typography>
            </Box>
          </Stack>
          <Box onClick={onFechar} sx={{ cursor: 'pointer', color: C.text3, mt: 0.3 }}>
            <FiX size={16} />
          </Box>
        </Stack>

        {/* A dúvida que todo mundo tem ao ver "limpar". Vem antes das opções
            de propósito — responder depois seria responder tarde. */}
        <Stack direction="row" spacing={0.9} sx={{
          p: '10px 12px', borderRadius: '8px', mb: 1.8,
          bgcolor: C.greenBg, border: `1px solid ${C.greenB}`,
        }}>
          <Box sx={{ color: C.greenD, mt: '2px' }}><FiAlertTriangle size={12} /></Box>
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.68rem', color: C.greenD, lineHeight: 1.5 }}>
            <strong>Nenhum contato é apagado.</strong> Só a conversa sai do chat —
            o contato continua em Contatos com as anotações, etiquetas e pastas.
            Se ele escrever de novo, a conversa volta sozinha.
          </Typography>
        </Stack>

        <Opcao
          ativa={modo === 'ALEM_DAS_RECENTES'}
          onClick={() => setModo('ALEM_DAS_RECENTES')}
          titulo="Manter só as mais recentes"
          descricao={foraDaLista > 0
            ? `${foraDaLista.toLocaleString('pt-BR')} conversas já estão fora da lista carregada.`
            : 'Limpa tudo que passar da quantidade escolhida.'}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small" type="number" value={manter}
              onChange={e => setManter(e.target.value)}
              onClick={e => e.stopPropagation()}
              inputProps={{ min: 1, max: 5000 }}
              sx={{
                width: 110,
                '& .MuiOutlinedInput-root': {
                  fontFamily: OUTFIT, fontSize: '0.8rem', bgcolor: C.card,
                },
              }}
            />
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.7rem', color: C.text2 }}>
              conversas mantidas
            </Typography>
          </Stack>
        </Opcao>

        <Opcao
          ativa={modo === 'ANTES_DE'}
          onClick={() => setModo('ANTES_DE')}
          titulo="Limpar por data"
          descricao="Limpa quem não troca mensagem há um tempo."
        >
          <Stack direction="row" spacing={0.6} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.6 }}>
            {PERIODOS.map(p => (
              <Box
                key={p.meses}
                onClick={e => { e.stopPropagation(); setMeses(p.meses); }}
                sx={{
                  px: 1.1, py: 0.5, borderRadius: '14px', cursor: 'pointer',
                  fontFamily: SYNE, fontSize: '0.66rem', fontWeight: 600,
                  border: `1px solid ${meses === p.meses ? C.green : C.bdr}`,
                  bgcolor: meses === p.meses ? C.green : C.card,
                  color: meses === p.meses ? '#fff' : C.text2,
                }}
              >
                {p.rotulo}
              </Box>
            ))}
          </Stack>
        </Opcao>

        {/* O modo manual não vive aqui: escolher conversa a conversa se faz
            na própria lista, onde dá pra ver nome, última mensagem e
            etiqueta. Um seletor de 1.141 itens dentro de um diálogo seria
            uma lista pior que a que já existe atrás dele. */}
        <Typography sx={{
          fontFamily: SYNE, fontSize: '0.66rem', color: C.text3,
          lineHeight: 1.5, mb: 1.8, px: 0.3,
        }}>
          Para escolher uma a uma, feche esta janela e use o menu de cada
          conversa na lista — lá você vê o nome e a última mensagem antes de decidir.
        </Typography>

        {erro && (
          <Typography sx={{
            fontFamily: SYNE, fontSize: '0.7rem', color: C.redD,
            bgcolor: C.redBg, border: `1px solid ${C.redB}`,
            borderRadius: '6px', p: '8px 10px', mb: 1.4,
          }}>
            {erro}
          </Typography>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onFechar} sx={{
            fontFamily: SYNE, fontSize: '0.75rem', fontWeight: 700,
            textTransform: 'none', color: C.text2,
          }}>
            Cancelar
          </Button>
          <Button
            onClick={aplicar}
            disabled={aplicando || contando || !previa}
            startIcon={aplicando
              ? <CircularProgress size={12} color="inherit" />
              : <FiTrash2 size={12} />}
            sx={{
              bgcolor: C.red, color: '#fff', px: 2, borderRadius: '7px',
              fontFamily: SYNE, fontSize: '0.75rem', fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: '#B91C1C' },
              '&.Mui-disabled': { bgcolor: C.bdr2, color: '#fff' },
            }}
          >
            {aplicando ? 'Limpando…'
              : contando ? 'Calculando…'
              : previa ? `Limpar ${previa.toLocaleString('pt-BR')} conversas`
              : 'Nada a limpar'}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
